<template>
  <div class="relative bg-transparent">
    <ul class="relative flex flex-col gap-y-7 lg:gap-y-6">
      <li v-for="[date, seances] in seancesByDays" :key="date">
        <header class="mb-2 font-medium lg:text-base">
          <UiTitle v-if="mode === 'date'" severity="h5">
            <UiDate :value="date" template="d" class="inline" />&nbsp;<UiDate
              :value="date"
              :template="isSameYear(date, new Date()) ? 'MMMM' : 'MMMM yyyy'"
              class="inline"
            />,&nbsp;<SeanceWeekDay :value="date" class="inline" />
          </UiTitle>
          <UiTitle v-else severity="h5">
            {{ date }}
          </UiTitle>
        </header>
        <SeanceList :value="seances" type="button" :mode @click="onSeanceOpen" />
      </li>
    </ul>

    <UiDialog :model-value="!!showingSeance" save-scroll-lock @update:model-value="onSeanceClose">
      <SeanceWidget
        v-if="showingSeance"
        :value="showingSeance"
        static-close-button
        @delete-seance="onSeanceDelete"
        @close="onSeanceClose"
      />
    </UiDialog>
  </div>
</template>

<script lang="ts" setup>
import { isSameYear } from 'date-fns'
import type { Undefinable } from 'ts-helpers'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { SeanceWidget } from '@/3_widgets/Seance'
import { useEvent } from '@/5_entities/Event'
import { type Seance, SeanceWeekDay } from '@/5_entities/Seance'
import { SeanceList } from '@/5_entities/Seance'
import { useGoBack, useQueryParamsOpenState } from '@/6_shared/composables'
import type { TimetableFilterMode } from '@/6_shared/model'
import { UiDate, UiDialog, UiTitle } from '@/6_shared/ui'

type PropType = {
  value: Seance[][]
  mode: TimetableFilterMode
}
const props = defineProps<PropType>()
const route = useRoute()
const showingSeance = ref<Undefinable<Seance['id']>>(undefined)
const { setParamInRouteQuery } = useQueryParamsOpenState('seance', () => {
  if (showingSeance.value) {
    showingSeance.value = undefined
  }

  setSeanceFromRouteQuery()
})
const { goBack } = useGoBack()
const setSeanceFromRouteQuery = () => {
  route.query?.seance && (showingSeance.value = Number(route.query.seance))
}
onMounted(() => setSeanceFromRouteQuery())
const onSeanceOpen = (id: Seance['id']) => {
  showingSeance.value = id

  setParamInRouteQuery(id.toString())
}
const { deleteEventSeance } = useEvent()
const onSeanceDelete = () => {
  showingSeance.value && deleteEventSeance(showingSeance.value)
  showingSeance.value = undefined
}

const onSeanceClose = () => {
  showingSeance.value = undefined
  goBack()
}

const seancesByDays = computed(() => {
  return props.value.reduce(
    (acc, item) => {
      const date = item[0].beginsAt
      const venue = item.find((seance) => seance.venue)!.venue!.title

      acc.set(props.mode === 'date' ? date : venue, item)

      return acc
    },
    new Map() as Map<string, Seance[]>
  )
})
</script>
